import React from 'react';
import {
  Activity, AlertTriangle, ArrowRight, Ban, BarChart3, Bike, Building2, Calendar, Camera,
  CheckCircle2, ClipboardList, Compass, CreditCard, Crown, Download, Eye, Factory, FileText,
  FolderOpen, Gift, Hand, HelpCircle, Hourglass, KeyRound, Link2, Lock, Mail, MapPin,
  Moon, Package, PartyPopper, Pencil, PenLine, Phone, Printer, Receipt, RefreshCw,
  Repeat, Rocket, Search, Settings, ShoppingBag, ShoppingCart, Sparkles, Store, Tag,
  ThumbsUp, TrendingUp, Truck, Undo2, Upload, User, Zap,
  XCircle, MinusCircle, Wrench, CircleDashed,
} from 'lucide-react';

/**
 * Inline icons, replacing the ~150 emoji this app used to render as icons.
 *
 * Emoji were the problem: they are drawn by the OS, so the same glyph is a flat Segoe outline on
 * Windows, glossy Apple art on a Mac and something else again on Android. Nothing else in the UI
 * changes shape by platform, so they always read as pasted-in rather than designed.
 *
 * These wrappers exist so a swap is a single token (one emoji -> `<IcoPackage />`) and so every icon
 * shares one baseline nudge — a bare lucide <svg> is inline and sits a couple of pixels low next
 * to text. Size defaults to 14 (roughly cap-height for our 12-14px body); pass `size` to override,
 * and `color` to tint (defaults to currentColor, so it inherits the surrounding text colour).
 */

const base = { verticalAlign: '-2px', flexShrink: 0 };

const mk = (C) => {
  const Wrapped = ({ size = 14, style, ...rest }) => (
    <C size={size} style={{ ...base, ...style }} aria-hidden="true" {...rest} />
  );
  Wrapped.displayName = `Ico(${C.displayName || C.name || 'icon'})`;
  return Wrapped;
};

export const IcoAlert      = mk(AlertTriangle);   // was WARNING SIGN
export const IcoArrowRight = mk(ArrowRight);      // was POINTING HAND
export const IcoBan        = mk(Ban);             // was PROHIBITED
export const IcoBike       = mk(Bike);            // was SCOOTER (out for delivery)
export const IcoBuilding   = mk(Building2);       // was OFFICE BUILDING
export const IcoCalendar   = mk(Calendar);        // was CALENDAR
export const IcoCamera     = mk(Camera);          // was CAMERA
export const IcoCart       = mk(ShoppingCart);    // was SHOPPING CART
export const IcoChart      = mk(BarChart3);       // was BAR CHART
export const IcoCheck      = mk(CheckCircle2);    // was CHECK MARK BUTTON
export const IcoClipboard  = mk(ClipboardList);   // was CLIPBOARD
export const IcoCompass    = mk(Compass);         // was COMPASS
export const IcoCrown      = mk(Crown);           // was CROWN (VIP tier)
export const IcoDoc        = mk(FileText);        // was PAGE
export const IcoDormant    = mk(Moon);            // was SLEEPING FACE
export const IcoEye        = mk(Eye);             // was EYE
export const IcoFactory    = mk(Factory);         // was FACTORY
export const IcoFolder     = mk(FolderOpen);      // was CARD INDEX DIVIDERS
export const IcoGift       = mk(Gift);            // was GIFT
export const IcoHand       = mk(Hand);            // was WAVING HAND
export const IcoHelp       = mk(HelpCircle);      // was QUESTION MARK
export const IcoKey        = mk(KeyRound);        // was LOCK WITH KEY
export const IcoLink       = mk(Link2);           // was LINK
export const IcoLock       = mk(Lock);            // was LOCK
export const IcoMail       = mk(Mail);            // was ENVELOPE
export const IcoNew        = mk(Sparkles);        // was NEW BUTTON
export const IcoNote       = mk(PenLine);         // was MEMO
export const IcoPackage    = mk(Package);         // was PACKAGE
export const IcoParty      = mk(PartyPopper);     // was PARTY POPPER
export const IcoPay        = mk(CreditCard);      // was CREDIT CARD
export const IcoPencil     = mk(Pencil);          // was PENCIL
export const IcoPhone      = mk(Phone);           // was TELEPHONE
export const IcoPin        = mk(MapPin);          // was ROUND PUSHPIN
export const IcoPrint      = mk(Printer);         // was PRINTER
export const IcoReceipt    = mk(Receipt);         // was RECEIPT
export const IcoDownload   = mk(Download);        // A78 — export / download
export const IcoUpload     = mk(Upload);          // A33 — upload a file (Amazon listings)
export const IcoRefresh    = mk(RefreshCw);       // was COUNTERCLOCKWISE ARROWS
export const IcoRepeat     = mk(Repeat);          // was REPEAT (Repeat tier / Reship)
export const IcoReturn     = mk(Undo2);           // was LEFTWARDS ARROW WITH HOOK
export const IcoRocket     = mk(Rocket);          // was ROCKET
export const IcoSearch     = mk(Search);          // was MAGNIFYING GLASS
export const IcoSettings   = mk(Settings);        // was GEAR
export const IcoShop       = mk(ShoppingBag);     // was SHOPPING BAGS
export const IcoStore      = mk(Store);           // was CONVENIENCE STORE
export const IcoTag        = mk(Tag);             // was LABEL
export const IcoThumbsUp   = mk(ThumbsUp);        // was THUMBS UP
export const IcoTrend      = mk(TrendingUp);      // was CHART INCREASING
export const IcoTruck      = mk(Truck);           // was DELIVERY TRUCK
export const IcoUser       = mk(User);            // was BUST IN SILHOUETTE
export const IcoWaiting    = mk(Hourglass);       // was HOURGLASS
export const IcoActivity   = mk(Activity);        // A88 — System Status
export const IcoXCircle    = mk(XCircle);         // A88 — major outage
export const IcoMinus      = mk(MinusCircle);     // A88 — degraded
export const IcoWrench     = mk(Wrench);          // A88 — maintenance
export const IcoNoData     = mk(CircleDashed);    // A88 — no recent activity
export const IcoZap        = mk(Zap);             // was HIGH VOLTAGE (Auto PO)
